import React, { useState } from 'react';
import { ResultsDisplayProps, HalalFoodCategory, AlcoholPolicyCategory } from '../types';

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results, transcript, baseline }) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const formatHalalFoodCategory = (category: HalalFoodCategory): string => {
    switch (category) {
      case 'all':
        return 'All food in property is always halal';
      case 'some':
        return 'Some food in property is always halal';
      case 'on_request':
        return 'Halal food available if requested in advance';
      case 'nearby':
        return 'Halal options within 500m (independent of property)';
      case 'none':
        return 'No halal provisions in property or nearby';
      case 'not_mentioned':
        return 'Not mentioned in conversation';
      default:
        return category;
    }
  };

  const formatAlcoholPolicyCategory = (category: AlcoholPolicyCategory): string => {
    switch (category) {
      case 'alcohol_free':
        return 'No alcohol served anywhere';
      case 'restaurant_only':
        return 'At least one alcohol-free restaurant';
      case 'room_only':
        return 'Alcohol removed from guest rooms upon request';
      case 'none':
        return 'No alcohol-free arrangements possible';
      case 'not_mentioned':
        return 'Not mentioned in conversation';
      default:
        return category;
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'all':
      case 'alcohol_free':
        return '#28a745'; // Green for best options
      case 'some':
      case 'restaurant_only':
      case 'room_only':
        return '#ffc107'; // Yellow for partial options
      case 'on_request':
      case 'nearby':
        return '#17a2b8'; // Blue for conditional options
      case 'none':
        return '#dc3545'; // Red for no options
      case 'not_mentioned':
        return '#6c757d'; // Gray for not mentioned
      default:
        return '#6c757d'; // Gray for unknown
    }
  };

  return (
    <div className="results-display">
      <h4>Compliance Results</h4>

      {/* Halal Food Section */}
      <div className="result-section">
        <h5>🍽️ Halal Food Availability</h5>
        <div
          className={`result-value ${baseline?.halalFood && baseline.halalFood !== results.halalFood ? 'mismatch' : ''}`}
          style={{ borderColor: getCategoryColor(results.halalFood) }}
        >
          {formatHalalFoodCategory(results.halalFood)}
          {baseline?.halalFood && baseline.halalFood !== results.halalFood && (
            <span className="mismatch-tag">Baseline: {formatHalalFoodCategory(baseline.halalFood as any)}</span>
          )}
        </div>
        {results.halalOptions && (
          <div className="facility-options tri-grid">
            <div className="facility-item">
              <span className={`facility-status ${results.halalOptions.all || 'uncertain'} ${baseline?.halalOptions?.all && baseline.halalOptions.all !== results.halalOptions.all ? 'mismatch' : ''}`}>
                {results.halalOptions.all === 'yes' ? '✓' : results.halalOptions.all === 'no' ? '✗' : '?'}
              </span>
              <span>All food is halal</span>
            </div>
            <div className="facility-item">
              <span className={`facility-status ${results.halalOptions.some || 'uncertain'} ${baseline?.halalOptions?.some && baseline.halalOptions.some !== results.halalOptions.some ? 'mismatch' : ''}`}>
                {results.halalOptions.some === 'yes' ? '✓' : results.halalOptions.some === 'no' ? '✗' : '?'}
              </span>
              <span>Some food is halal (always on menu)</span>
            </div>
            <div className="facility-item">
              <span className={`facility-status ${results.halalOptions.onRequest || 'uncertain'} ${baseline?.halalOptions?.onRequest && baseline.halalOptions.onRequest !== results.halalOptions.onRequest ? 'mismatch' : ''}`}>
                {results.halalOptions.onRequest === 'yes' ? '✓' : results.halalOptions.onRequest === 'no' ? '✗' : '?'}
              </span>
              <span>Halal on request (advance notice)</span>
            </div>
            <div className="facility-item">
              <span className={`facility-status ${results.halalOptions.nearby || 'uncertain'} ${baseline?.halalOptions?.nearby && baseline.halalOptions.nearby !== results.halalOptions.nearby ? 'mismatch' : ''}`}>
                {results.halalOptions.nearby === 'yes' ? '✓' : results.halalOptions.nearby === 'no' ? '✗' : '?'}
              </span>
              <span>Nearby halal dining (walking distance)</span>
            </div>
          </div>
        )}
      </div>

      {/* Alcohol Policies Section */}
      <div className="result-section">
        <h5>🍷 Alcohol Policies</h5>
        <div
          className={`result-value ${baseline?.alcoholPolicies && baseline.alcoholPolicies !== results.alcoholPolicies ? 'mismatch' : ''}`}
          style={{ borderColor: getCategoryColor(results.alcoholPolicies) }}
        >
          {formatAlcoholPolicyCategory(results.alcoholPolicies)}
          {baseline?.alcoholPolicies && baseline.alcoholPolicies !== results.alcoholPolicies && (
            <span className="mismatch-tag">Baseline: {formatAlcoholPolicyCategory(baseline.alcoholPolicies as any)}</span>
          )}
        </div>
        {results.alcoholOptions && (
          <div className="facility-options tri-grid">
            <div className="facility-item">
              <span className={`facility-status ${results.alcoholOptions.property || 'uncertain'} ${baseline?.alcoholOptions?.property && baseline.alcoholOptions.property !== results.alcoholOptions.property ? 'mismatch' : ''}`}>
                {results.alcoholOptions.property === 'yes' ? '✓' : results.alcoholOptions.property === 'no' ? '✗' : '?'}
              </span>
              <span>Alcohol-free property</span>
            </div>
            <div className="facility-item">
              <span className={`facility-status ${results.alcoholOptions.restaurant || 'uncertain'} ${baseline?.alcoholOptions?.restaurant && baseline.alcoholOptions.restaurant !== results.alcoholOptions.restaurant ? 'mismatch' : ''}`}>
                {results.alcoholOptions.restaurant === 'yes' ? '✓' : results.alcoholOptions.restaurant === 'no' ? '✗' : '?'}
              </span>
              <span>Alcohol-free restaurant(s)</span>
            </div>
            <div className="facility-item">
              <span className={`facility-status ${results.alcoholOptions.room || 'uncertain'} ${baseline?.alcoholOptions?.room && baseline.alcoholOptions.room !== results.alcoholOptions.room ? 'mismatch' : ''}`}>
                {results.alcoholOptions.room === 'yes' ? '✓' : results.alcoholOptions.room === 'no' ? '✗' : '?'}
              </span>
              <span>Alcohol can be removed from rooms</span>
            </div>
          </div>
        )}
      </div>

      {/* Leisure Facilities Section */}
      <div className="result-section">
        <h5>🏊 Leisure Facilities</h5>
        <div className="facility-options">
          <div className="facility-item">
            <span className={`facility-status ${results.leisureFacilities.modestSwimwear}`}>
              {results.leisureFacilities.modestSwimwear === 'yes' ? '✓' :
               results.leisureFacilities.modestSwimwear === 'no' ? '✗' : '?'}
            </span>
            <span>Can women wear modest swimwear (burkini) in pool/spa areas?</span>
          </div>

          <div className="facility-item">
            <span className={`facility-status ${results.leisureFacilities.womenOnlyFacilities}`}>
              {results.leisureFacilities.womenOnlyFacilities === 'yes' ? '✓' :
               results.leisureFacilities.womenOnlyFacilities === 'no' ? '✗' : '?'}
            </span>
            <span>Are there any leisure facilities restricted to women only?</span>
          </div>

          <div className="facility-item">
            <span className={`facility-status ${results.leisureFacilities.privateHire}`}>
              {results.leisureFacilities.privateHire === 'yes' ? '✓' :
               results.leisureFacilities.privateHire === 'no' ? '✗' : '?'}
            </span>
            <span>Can leisure facilities be privately hired/booked?</span>
          </div>
        </div>
      </div>

      {/* Transcript Section */}
      {transcript && transcript.trim() && (
        <div className="result-section">
          <h5>📞 Call Transcript</h5>
          <button
            className="transcript-toggle"
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
          </button>
          {showTranscript && (
            <div className="transcript-content">
              <pre>{transcript}</pre>
            </div>
          )}
        </div>
      )}

      {/* Extra Info Section */}
      {results.meta && (
        <div className="result-section">
          <h5>📝 Extra Info</h5>
          <div className="extra-summary">{results.meta.summary || '—'}</div>
          <div className="extra-row">
            <div className="extra-item">
              <span className="extra-label">Sentiment</span>
              <span className="extra-value">{results.meta.sentiment || '—'}</span>
            </div>
            <div className="extra-item">
              <span className="extra-label">Wants Callback</span>
              <span className="extra-value">
                {results.meta.wantsCallback === 'yes' ? '✓' : results.meta.wantsCallback === 'no' ? '✗' : '?'}
              </span>
            </div>
            <div className="extra-item">
              <span className="extra-label">Wants Human</span>
              <span className="extra-value">
                {results.meta.wantsHuman === 'yes' ? '✓' : results.meta.wantsHuman === 'no' ? '✗' : '?'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Flags (Follow-up Action) */}
      {results.meta && (
        <div className="result-section">
          <h5>🚩 Flags</h5>
          <div className="followup-row">
            <div className={`followup-pill ${results.meta.followUpAction === 'update_data' ? 'active' : ''}`}>Update data</div>
            <div className={`followup-pill ${results.meta.followUpAction === 'call_back' ? 'active' : ''}`}>Call back / contact</div>
            <div className={`followup-pill ${results.meta.followUpAction === 'human_needed' ? 'active' : ''}`}>Human needed</div>
          </div>
        </div>
      )}

      <style>{`
        .results-display {
          padding: 1.5rem;
          background: var(--hb-white);
          border: 1px solid var(--hb-border);
          border-radius: 8px;
        }

        .results-display h4 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.1rem;
        }

        .result-section {
          margin-bottom: 1.5rem;
        }

        .result-section h5 {
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          color: var(--hb-sea-dark);
          font-weight: 700;
        }

        .result-value {
          padding: 1rem;
          background: var(--hb-white);
          border-radius: 4px;
          border-left: 4px solid;
          font-weight: 500;
          color: var(--hb-slate);
        }

        .facility-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .facility-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: white;
          border-radius: 4px;
        }

        .tri-grid .facility-item {
          border: 1px solid var(--hb-border);
        }

        .facility-status {
          font-size: 1.2rem;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
        }
        .facility-status.mismatch {
          text-decoration: underline;
          text-decoration-thickness: 2px;
          text-decoration-color: #dc3545;
        }

        .result-value.mismatch {
          border-color: #dc3545 !important;
          position: relative;
        }
        .mismatch-tag {
          display: inline-block;
          margin-left: 0.5rem;
          font-size: 0.8rem;
          color: #dc3545;
          font-weight: 600;
        }

        .facility-status.yes {
          color: var(--hb-sea-dark);
        }

        .facility-status.no {
          color: var(--hb-orange);
        }

        .facility-status.uncertain {
          color: #a0b4bf;
        }

        .facility-item span:last-child {
          flex: 1;
          font-size: 0.9rem;
          color: #495057;
        }

        .transcript-toggle {
          background: var(--hb-sea);
          color: var(--hb-white);
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .transcript-toggle:hover {
          background: var(--hb-sea-dark);
        }

        .transcript-content {
          background: var(--hb-white);
          padding: 1rem;
          border-radius: 4px;
          border: 1px solid var(--hb-border);
          max-height: 400px;
          overflow-y: auto;
        }

        .transcript-content pre {
          margin: 0;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          line-height: 1.4;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .extra-summary {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 0.75rem;
          color: #343a40;
          margin-bottom: 0.6rem;
        }
        .extra-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(120px, 1fr));
          gap: 0.6rem;
        }
        .extra-item {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 0.6rem 0.75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .extra-label {
          font-size: 0.82rem;
          color: #6c757d;
        }
        .extra-value {
          font-size: 0.95rem;
          color: #343a40;
          font-weight: 600;
        }
        .followup-row {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .followup-pill {
          padding: 0.4rem 0.6rem;
          border-radius: 999px;
          background: #e9ecef;
          color: #495057;
          border: 1px solid #ced4da;
          font-size: 0.85rem;
        }
        .followup-pill.active {
          background: #0d6efd;
          color: white;
          border-color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default ResultsDisplay;



